import React, { useState, useEffect, useContext, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faSearch, 
  faPlus,
  faMapMarkerAlt,
  faCrown,
  faTrash,
  faCalendarAlt,
  faBuilding,
  faHotel,
  faUserFriends,
  faChevronDown,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { 
  query, 
  collection, 
  getDocs, 
} from 'firebase/firestore';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import LocationSearchModal from './LocationSearchModal';
import UserContext from '../../context/UserContext';
import TourContext from '../../context/TourContext';
import { useNavigate } from 'react-router-dom';
import styles from './AddScheduleModal.module.css';

const AddScheduleModal = ({ isOpen, onClose, onSave, selectedTime, eventId, dateId, editingSchedule, contacts = [], eventType = 'tour' }) => {
  const navigate = useNavigate();
  const { userData } = useContext(UserContext);
  const { selectedEvent, getSelectedDate } = useContext(TourContext);
  
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [visibility, setVisibility] = useState('all');
  const [visibilityType, setVisibilityType] = useState('all');
  const [customVisibility, setCustomVisibility] = useState([]);
  const [showCustomSelection, setShowCustomSelection] = useState(false);
  const [fromLocation, setFromLocation] = useState(null);
  const [toLocation, setToLocation] = useState(null);
  const [showLocationSearch, setShowLocationSearch] = useState(false);
  const [isFromLocation, setIsFromLocation] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState([]);
  
  // New state for hotel dropdown
  const [isLoading, setIsLoading] = useState(false);
  const [hotels, setHotels] = useState([]);
  const [showHotelDropdown, setShowHotelDropdown] = useState(false);
  const [currentLocationType, setCurrentLocationType] = useState(null);
  const hotelDropdownRef = useRef(null);

  // Initialize dates based on event date or editing schedule
  useEffect(() => {
    // If editing a schedule, populate form with existing data
    if (editingSchedule) {
      setTitle(editingSchedule.title || '');
      setNotes(editingSchedule.notes || '');
      
      // Set dates from schedule
      if (editingSchedule.startDate) {
        setStartDate(new Date(editingSchedule.startDate));
      }
      
      if (editingSchedule.endDate) {
        setEndDate(new Date(editingSchedule.endDate));
      }
      
      // Set times
      setStartTime(editingSchedule.startTime || '');
      setEndTime(editingSchedule.endTime || '');
      
      // Set confirmed status
      setIsConfirmed(editingSchedule.isConfirmed || false);
      
      // Set visibility
      setVisibilityType(editingSchedule.visibilityType || 'all');
      setShowCustomSelection(editingSchedule.visibilityType === 'custom');
      
      if (editingSchedule.visibility && Array.isArray(editingSchedule.visibility)) {
        setCustomVisibility(editingSchedule.visibility);
      }
      
      // Set locations
      setFromLocation(editingSchedule.fromLocation || null);
      setToLocation(editingSchedule.toLocation || null);
  
      // Set selected contacts
      setSelectedContacts(editingSchedule.assignedContacts || []);
      
      return; // Skip the event date logic if editing
    }
  
    
    // Otherwise initialize with event date
    const eventDate = getSelectedDate();
    if (eventDate && eventDate.date) {
      // Convert Firestore timestamp to Date
      let dateObj;
      if (eventDate.date.seconds) {
        dateObj = new Date(eventDate.date.seconds * 1000);
      } else if (typeof eventDate.date.toDate === 'function') {
        dateObj = eventDate.date.toDate();
      } else {
        dateObj = new Date(eventDate.date);
      }
      
      // Check if date is valid
      if (dateObj && !isNaN(dateObj.getTime())) {
        setStartDate(dateObj);
        setEndDate(dateObj);
      } else {
        setStartDate(new Date());
        setEndDate(new Date());
      }
    }
  }, [editingSchedule, getSelectedDate]);

  useEffect(() => {
    if (selectedTime) {
      setStartTime(selectedTime);
      const [hours] = selectedTime.split(':');
      const nextHour = (parseInt(hours) + 1) % 24;
      setEndTime(`${nextHour.toString().padStart(2, '0')}:00`);
    }
  }, [selectedTime]);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (hotelDropdownRef.current && !hotelDropdownRef.current.contains(event.target)) {
        setShowHotelDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Handle date changes with validation
  const handleStartDateChange = (date) => {
    const eventDate = getSelectedDate();
    let dateObj = new Date();
    
    // Convert event date from Firestore timestamp
    if (eventDate && eventDate.date) {
      if (eventDate.date.seconds) {
        dateObj = new Date(eventDate.date.seconds * 1000);
      } else if (typeof eventDate.date.toDate === 'function') {
        dateObj = eventDate.date.toDate();
      } else {
        dateObj = new Date(eventDate.date);
      }
    }
    
    // Reset time part for comparison
    dateObj.setHours(0, 0, 0, 0);
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);
    
    // Don't allow dates before event date
    if (compareDate < dateObj) {
      alert("Start date cannot be before the event date");
      return;
    }
    
    setStartDate(date);
    
    // If end date is now before start date, update it
    if (endDate < date) {
      setEndDate(date);
    }
  };
  
  const handleEndDateChange = (date) => {
    const startCompare = new Date(startDate);
    startCompare.setHours(0, 0, 0, 0);
    
    const endCompare = new Date(date);
    endCompare.setHours(0, 0, 0, 0);
    
    // Don't allow end date before start date
    if (endCompare < startCompare) {
      alert("End date cannot be before the start date");
      return;
    }
    
    setEndDate(date);
  };

  const handleQuickSelectLocation = async (locationType, locationSource) => {
    const eventDate = getSelectedDate();
    
    if (locationSource === 'venue') {
      const location = eventDate?.event?.venueDetails?.venueData;
      if (location) {
        formatAndSetLocation(location, locationType);
      }
    } else if (locationSource === 'hotel') {
      try {
        // Fetch hotels from the hotels subcollection
        if (selectedEvent?.id && eventDate?.id) {
          setIsLoading(true);
          setCurrentLocationType(locationType);
          const db = getFirestoreInstance();
          const collectionName = selectedEvent.type === 'tour' ? 'tours' : 'shows';
          
          // Query the hotels subcollection for this event date - get all hotels
          const hotelsQuery = query(
            collection(db, collectionName, selectedEvent.id, 'eventDates', eventDate.id, 'hotels')
          );
          
          const hotelsSnapshot = await getDocs(hotelsQuery);
          
          if (!hotelsSnapshot.empty) {
            // Store all hotels in state
            const hotelsList = hotelsSnapshot.docs.map(doc => {
              const hotelData = doc.data();
              return {
                id: doc.id,
                name: hotelData.venueDetails?.venueData?.name || 'Hotel',
                venueData: hotelData.venueDetails?.venueData || null
              };
            });
            
            setHotels(hotelsList);
            
            // If there's only one hotel, select it directly
            if (hotelsList.length === 1 && hotelsList[0].venueData) {
              formatAndSetLocation(hotelsList[0].venueData, locationType);
            } else {
              // Show dropdown for multiple hotels
              setShowHotelDropdown(true);
            }
          } else {
            alert('No hotels found for this event date');
          }
        }
      } catch (error) {
        console.error('Error fetching hotels from subcollection:', error);
        alert('Error fetching hotels. Please try again.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleSelectHotel = (hotel) => {
    if (hotel.venueData) {
      formatAndSetLocation(hotel.venueData, currentLocationType);
    }
    setShowHotelDropdown(false);
  };
  
  // Helper function to format and set the location
  const formatAndSetLocation = (location, locationType) => {
    // Transform the location data to match the expected format
    const formattedLocation = {
      id: location.id || '',
      name: location.name || location.displayName || '',
      displayName: location.displayName || location.name || '',
      formattedAddress: location.formattedAddress || location.address || '',
      shortFormattedAddress: location.shortFormattedAddress || location.address || '',
      location: location.location || null,
      latitude: location.location?.latitude || location.latitude,
      longitude: location.location?.longitude || location.longitude,
      placeCoverPhotoUrl: location.placeCoverPhotoUrl || '',
      googleMapsUri: location.googleMapsUri || '',
    };
  
    if (locationType === 'from') {
      setFromLocation(formattedLocation);
    } else {
      setToLocation(formattedLocation);
    }
  };

  const handleContactToggle = (contactId) => {
    setSelectedContacts(prev => {
      if (prev.includes(contactId)) {
        return prev.filter(id => id !== contactId);
      } else {
        return [...prev, contactId];
      }
    });
  };

  const getEventMembers = async () => {
    if (!selectedEvent) return [];
    
    const members = [];
    
    try {
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      // Query assigned users from the assignedUsers subcollection
      const assignedUsersQuery = query(
        collection(db, collectionName, eventId, 'assignedUsers')
      );
      
      const assignedUsersSnapshot = await getDocs(assignedUsersQuery);
      
      assignedUsersSnapshot.forEach(doc => {
        const userData = doc.data();
        members.push({
          userId: doc.id,
          fullName: userData.fullName || userData.userName || doc.id,
          userType: userData.role || userData.userType || 'Member'
        });
      });
      
      return members;
    } catch (error) {
      console.error('Error fetching event members:', error);
      
      // Fallback to using the legacy fields if available
      if (selectedEvent.createdBy) {
        members.push({
          userId: selectedEvent.createdBy.userId,
          fullName: selectedEvent.createdBy.fullName,
          userType: 'Manager'
        });
      }
      
      // Add artists from artistDetails (legacy)
      if (Array.isArray(selectedEvent.artistDetails)) {
        selectedEvent.artistDetails.forEach(artist => {
          members.push({
            userId: artist.userId,
            fullName: artist.fullName,
            userType: 'Artist'
          });
        });
      }
      
      // Add crew from crewDetails (legacy)
      if (Array.isArray(selectedEvent.crewDetails)) {
        selectedEvent.crewDetails.forEach(crew => {
          members.push({
            userId: crew.userId,
            fullName: crew.fullName,
            userType: 'Crew'
          });
        });
      }
      
      return members;
    }
  };

  const getMembersByType = async (type) => {
    const members = await getEventMembers();
    switch (type) {
      case 'artist':
        return members.filter(m => m.userType === 'Artist').map(m => m.userId);
      case 'crew':
        return members.filter(m => m.userType === 'Crew').map(m => m.userId);
      case 'me':
        return [userData.userId];
      case 'custom':
        return customVisibility;
      default:
        return [];
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const visibilityMembers = userData?.membershipTier === 'Elite' 
      ? await getMembersByType(visibilityType) 
      : [];
  
    // Combine date and time
    const startDateTime = new Date(startDate);
    const [startHours, startMinutes] = startTime.split(':');
    startDateTime.setHours(parseInt(startHours, 10), parseInt(startMinutes, 10), 0, 0);
  
    const endDateTime = new Date(endDate);
    const [endHours, endMinutes] = endTime.split(':');
    endDateTime.setHours(parseInt(endHours, 10), parseInt(endMinutes, 10), 0, 0);
  
    // Create the scheduleData object with all required fields - using only docId
    const scheduleData = {
      // For existing schedules, use docId
      ...(editingSchedule && { docId: editingSchedule.docId }),
      
      title: title || '',
      notes: notes || '',
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      startTime: startTime || '',
      endTime: endTime || '',
      startDateTime: startDateTime.toISOString(),
      endDateTime: endDateTime.toISOString(),
      isConfirmed: !!isConfirmed,
      visibility: userData?.membershipTier === 'Elite' ? visibilityMembers : [],
      visibilityType: userData?.membershipTier === 'Elite' ? visibilityType : 'all',
      // Replace undefined with null for location objects
      fromLocation: fromLocation || null,
      toLocation: toLocation || null,
      assignedContacts: selectedContacts || [],
      createdAt: editingSchedule ? (editingSchedule.createdAt || new Date().toISOString()) : new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      creatorId: editingSchedule ? (editingSchedule.creatorId || userData.userId) : userData.userId
    };
    
    onSave(scheduleData, !!editingSchedule);
    onClose();
  };

  const handleLocationSelect = (location) => {
    if (isFromLocation) {
      setFromLocation(location);
    } else {
      setToLocation(location);
    }
    setShowLocationSearch(false);
  };

  const handleClearLocation = (isFrom) => {
    if (isFrom) {
      setFromLocation(null);
    } else {
      setToLocation(null);
    }
  };

  const handleUpgrade = () => {
    navigate('/membership');
  };

  // Toggle function for confirmed switch
  const handleConfirmedToggle = () => {
    setIsConfirmed(!isConfirmed);
  };

  // Handle outside clicks - only for modal closing
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Hotel Dropdown Component
  const HotelDropdown = () => (
    <div 
      ref={hotelDropdownRef}
      className={styles.hotelDropdownContainer}
    >
      {isLoading ? (
        <div className={styles.dropdownLoading}>Loading hotels...</div>
      ) : (
        <ul className={styles.hotelDropdown}>
          {hotels.map(hotel => (
            <li 
              key={hotel.id} 
              className={styles.hotelDropdownItem}
              onClick={() => handleSelectHotel(hotel)}
            >
              {hotel.name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  // Upgrade Modal Component
  const UpgradeModal = () => (
    <div className={styles.upgradeModalOverlay}>
      <div className={styles.upgradeModal}>
        <h3 className={styles.upgradeModalTitle}>Upgrade to Elite</h3>
        <p className={styles.upgradeModalMessage}>
          Upgrade to 'Elite' membership to set custom visibility for your schedules
        </p>
        <div className={styles.upgradeModalActions}>
          <button
            type="button"
            onClick={() => setShowUpgradeModal(false)}
            className={styles.cancelButton}
          >
            Close
          </button>
          <button
            type="button"
            onClick={handleUpgrade}
            className={styles.submitButton}
          >
            Upgrade
          </button>
        </div>
      </div>
    </div>
  );

  const LocationDisplay = ({ location, onClear, label }) => {
    if (!location) return null;
    
    return (
      <div className={styles.selectedLocationDisplay}>
        {location.placeCoverPhotoUrl && (
          <img
            src={location.placeCoverPhotoUrl}
            alt={location.displayName}
            className={styles.locationThumbnail}
          />
        )}
        <div className={styles.locationInfo}>
          <p className={styles.locationDisplayName}>{location.displayName}</p>
          <p className={styles.locationDisplayAddress}>{location.shortFormattedAddress || location.formattedAddress}</p>
        </div>
        <button
          type="button"
          className={styles.clearLocationButton}
          onClick={onClear}
          title="Remove location"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <>
     <div className={styles.modalOverlay} onClick={handleOverlayClick}>
        <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
          <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>{editingSchedule ? 'Edit Schedule' : 'Add Schedule'}</h2>
            <button className={styles.closeButton} onClick={onClose}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGroup}>
              <label htmlFor="title">Title</label>
              <input
                id="title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter schedule title"
                className={styles.formInput}
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="notes">Notes</label>
              <textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any notes..."
                className={styles.formTextarea}
              />
            </div>
            
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Start Date</label>
                <div className={styles.datePickerWrapper}>
                  <DatePicker
                    selected={startDate}
                    onChange={handleStartDateChange}
                    className={styles.datePicker}
                    dateFormat="MMMM d, yyyy"
                  />
                  <FontAwesomeIcon icon={faCalendarAlt} className={styles.datePickerIcon} />
                </div>
              </div>
              <div className={styles.formGroup}>
                <label>End Date</label>
                <div className={styles.datePickerWrapper}>
                  <DatePicker
                    selected={endDate}
                    onChange={handleEndDateChange}
                    className={styles.datePicker}
                    dateFormat="MMMM d, yyyy"
                  />
                  <FontAwesomeIcon icon={faCalendarAlt} className={styles.datePickerIcon} />
                </div>
              </div>
            </div>

            <div className={styles.timeInputs}>
              <div className={styles.formGroup}>
                <label htmlFor="startTime">Start Time</label>
                <input
                  id="startTime"
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className={styles.formInput}
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="endTime">End Time</label>
                <input
                  id="endTime"
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className={styles.formInput}
                  required
                />
              </div>
            </div>

            <div className={styles.locationSection}>
              <h3>Location Details</h3>
              
              <div className={styles.formGroup}>
                <label>From Location</label>
                
                {/* Quick select buttons */}
                <div className={styles.quickSelectContainer}>
                  <button
                    type="button"
                    className={styles.quickSelectButton}
                    onClick={() => handleQuickSelectLocation('from', 'venue')}
                  >
                    <FontAwesomeIcon icon={faBuilding} />
                    <span>Select Venue</span>
                  </button>
                  <div className={styles.hotelSelectWrapper}>
                    <button
                      type="button"
                      className={styles.quickSelectButton}
                      onClick={() => handleQuickSelectLocation('from', 'hotel')}
                      disabled={isLoading}
                    >
                      <FontAwesomeIcon icon={faHotel} />
                      <span>Select Hotel</span>
                      <FontAwesomeIcon icon={faChevronDown} className={styles.dropdownIcon} />
                    </button>
                    {showHotelDropdown && currentLocationType === 'from' && <HotelDropdown />}
                  </div>
                </div>

                <button
                  type="button"
                  className={styles.locationButton}
                  onClick={() => {
                    setIsFromLocation(true);
                    setShowLocationSearch(true);
                  }}
                >
                  <FontAwesomeIcon icon={faSearch} />
                  {fromLocation?.displayName || 'Search location'}
                </button>
                <LocationDisplay 
                  location={fromLocation} 
                  onClear={() => handleClearLocation(true)}
                  label="From"
                />
              </div>

              <div className={styles.formGroup}>
                <label>To Location</label>
                
                {/* Quick select buttons */}
                <div className={styles.quickSelectContainer}>
                  <button
                    type="button"
                    className={styles.quickSelectButton}
                    onClick={() => handleQuickSelectLocation('to', 'venue')}
                  >
                    <FontAwesomeIcon icon={faBuilding} />
                    <span>Select Venue</span>
                  </button>
                  <div className={styles.hotelSelectWrapper}>
                    <button
                      type="button"
                      className={styles.quickSelectButton}
                      onClick={() => handleQuickSelectLocation('to', 'hotel')}
                      disabled={isLoading}
                    >
                      <FontAwesomeIcon icon={faHotel} />
                      <span>Select Hotel</span>
                      <FontAwesomeIcon icon={faChevronDown} className={styles.dropdownIcon} />
                    </button>
                    {showHotelDropdown && currentLocationType === 'to' && <HotelDropdown />}
                  </div>
                </div>

                <button
                  type="button"
                  className={styles.locationButton}
                  onClick={() => {
                    setIsFromLocation(false);
                    setShowLocationSearch(true);
                  }}
                >
                  <FontAwesomeIcon icon={faSearch} />
                  {toLocation?.displayName || 'Search location'}
                </button>
                <LocationDisplay 
                  location={toLocation} 
                  onClear={() => handleClearLocation(false)}
                  label="To"
                />
              </div>
            </div>

            {/* Assign Contacts Section */}
            {contacts.length > 0 && (
              <div className={styles.contactSection}>
                <h3>
                  <FontAwesomeIcon icon={faUserFriends} />
                  Assign Contacts
                </h3>
                <div className={styles.contactList}>
                  {contacts.map((contact) => (
                    <div key={contact.id} className={styles.contactItem}>
                      <label className={styles.contactLabel}>
                        <input
                          type="checkbox"
                          checked={selectedContacts.includes(contact.id)}
                          onChange={() => handleContactToggle(contact.id)}
                          className={styles.contactCheckbox}
                        />
                        <div className={styles.contactInfo}>
                          <span className={styles.contactName}>{contact.name}</span>
                          <span className={styles.contactRole}>
                            {contact.role.charAt(0).toUpperCase() + contact.role.slice(1)}
                          </span>
                        </div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className={styles.switchContainer}>
              <label htmlFor="confirmed">Confirmed</label>
              <label className={styles.switch}>
                <input
                  type="checkbox"
                  id="confirmed"
                  checked={isConfirmed}
                  onChange={handleConfirmedToggle}
                />
                <span className={styles.slider}></span>
              </label>
            </div>

            <div className={styles.formGroup}>
              <div className={styles.visibilityHeader}>
                <label>Visibility</label>
                {userData?.membershipTier !== 'Elite' && (
                  <button 
                    type="button"
                    className={styles.upgradeButton}
                    onClick={() => setShowUpgradeModal(true)}
                  >
                    <FontAwesomeIcon icon={faCrown} />
                    Upgrade
                  </button>
                )}
              </div>

              {userData?.membershipTier === 'Elite' ? (
                <div className={styles.visibilityControl}>
                  <div className={styles.visibilityOptions}>
                    <label className={styles.visibilityOption}>
                      <input
                        type="radio"
                        name="visibility"
                        value="all"
                        checked={visibilityType === 'all'}
                        onChange={(e) => {
                          setVisibilityType(e.target.value);
                          setShowCustomSelection(false);
                        }}
                      />
                      <span>All Members</span>
                    </label>
                    <label className={styles.visibilityOption}>
                      <input
                        type="radio"
                        name="visibility"
                        value="artist"
                        checked={visibilityType === 'artist'}
                        onChange={(e) => {
                          setVisibilityType(e.target.value);
                          setShowCustomSelection(false);
                        }}
                      />
                      <span>Artists Only</span>
                    </label>
                    <label className={styles.visibilityOption}>
                      <input
                        type="radio"
                        name="visibility"
                        value="crew"
                        checked={visibilityType === 'crew'}
                        onChange={(e) => {
                          setVisibilityType(e.target.value);
                          setShowCustomSelection(false);
                        }}
                      />
                      <span>Crew Only</span>
                    </label>
                    <label className={styles.visibilityOption}>
                      <input
                        type="radio"
                        name="visibility"
                        value="me"
                        checked={visibilityType === 'me'}
                        onChange={(e) => {
                          setVisibilityType(e.target.value);
                          setShowCustomSelection(false);
                        }}
                      />
                      <span>Only Me</span>
                    </label>
                    <label className={styles.visibilityOption}>
                      <input
                        type="radio"
                        name="visibility"
                        value="custom"
                        checked={visibilityType === 'custom'}
                        onChange={(e) => {
                          setVisibilityType(e.target.value);
                          setShowCustomSelection(true);
                        }}
                      />
                      <span>Custom</span>
                    </label>
                  </div>

                  {showCustomSelection && (
                    <div className={styles.customVisibilitySelector}>
                      <p className={styles.customVisibilityTitle}>Select specific members:</p>
                      {getEventMembers().then(members => members.map(member => {
                        const isManager = member.userType === 'Manager';
                        return (
                          <div 
                            key={member.userId} 
                            className={`${styles.customVisibilityOption} ${isManager ? styles.manager : ''}`}
                          >
                            <input
                              type="checkbox"
                              id={`custom-${member.userId}`}
                              checked={isManager || customVisibility.includes(member.userId)}
                              onChange={(e) => {
                                if (!isManager) {
                                  if (e.target.checked) {
                                    setCustomVisibility([...customVisibility, member.userId]);
                                  } else {
                                    setCustomVisibility(customVisibility.filter(id => id !== member.userId));
                                  }
                                }
                              }}
                              disabled={isManager}
                            />
                            <label htmlFor={`custom-${member.userId}`}>
                              {member.fullName} ({member.userType})
                            </label>
                          </div>
                        );
                      }))}
                    </div>
                  )}
                </div>
              ) : (
                <div className={styles.visibilityInfo}>
                  <span>Visible to all members</span>
                  <span className={styles.eliteTag}>Elite feature</span>
                </div>
              )}
            </div>

            <div className={styles.formActions}>
              <button
                type="button"
                onClick={onClose}
                className={styles.cancelButton}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className={styles.submitButton}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin className={styles.spinnerIcon} />
                    {editingSchedule ? 'Updating...' : 'Saving...'}
                  </>
                ) : (
                  editingSchedule ? 'Update' : 'Save'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {showLocationSearch && (
        <LocationSearchModal
          isOpen={showLocationSearch}
          onClose={() => setShowLocationSearch(false)}
          onSelect={handleLocationSelect}
          title={isFromLocation ? 'Select From Location' : 'Select To Location'}
        />
      )}

      {showUpgradeModal && <UpgradeModal />}
      
      {/* Loading overlay */}
      {isLoading && (
        <div className={styles.loadingOverlay}>
          <div className={styles.loadingContent}>
            <FontAwesomeIcon icon={faSpinner} spin size="2x" />
            <p>{editingSchedule ? 'Updating schedule...' : 'Adding schedule...'}</p>
          </div>
        </div>
      )}
    </>
  );
};

export default AddScheduleModal;